import { MEDIA_LIMITS } from '../media/mediaLimits';
import type { ZipEntry } from '../zip/reader';
import { archiveRoot, categoryFromPath, type ArchiveCategory, type MediaFacts } from './layout';

// 9.18.10.4 · D-830 — which files in a person's own Instagram download can go
// on their profile: every photo and video they posted — posts and carousel
// items, Reels, stories, IGTV, archived posts and profile photos.
//
// Only the download's own `media/` folder is read: that is what the person
// posted. Photos and videos inside conversations belong to other people too,
// so they are never offered; nor is "recently deleted", which the person chose
// to remove. A download Meta split into several ZIPs is read as one.
//
// The time is the archive's own `creation_timestamp` when its JSON names the
// file; otherwise the year and month of the folder it sits in. Either is the
// archive's declaration, not proof of when or by whom.

export type ArchiveMediaKind = 'image' | 'video';
export type { ArchiveCategory };

export type ArchiveMedia = {
  /** Which of the chosen ZIP files holds it. */
  source: number;
  entry: ZipEntry;
  /** The path inside the download, without any wrapping folder; stored as `source_key`. */
  key: string;
  kind: ArchiveMediaKind;
  contentType: string;
  extension: string;
  category: ArchiveCategory;
  takenAt: Date | null;
  /** True when the time came from the archive's JSON rather than the folder name. */
  exactTime: boolean;
  /** Over the upload ceiling for its kind; listed so the person knows, but not selectable. */
  tooLarge: boolean;
};

export type ArchiveCatalog = {
  media: ArchiveMedia[];
  skipped: { outsideMedia: number; unsupported: number; unreadable: number; deleted: number };
};

const TYPES: Record<string, { kind: ArchiveMediaKind; contentType: string; extension: string }> = {
  jpg: { kind: 'image', contentType: 'image/jpeg', extension: 'jpg' },
  jpeg: { kind: 'image', contentType: 'image/jpeg', extension: 'jpg' },
  png: { kind: 'image', contentType: 'image/png', extension: 'png' },
  webp: { kind: 'image', contentType: 'image/webp', extension: 'webp' },
  heic: { kind: 'image', contentType: 'image/heic', extension: 'heic' },
  heif: { kind: 'image', contentType: 'image/heic', extension: 'heic' },
  gif: { kind: 'image', contentType: 'image/gif', extension: 'gif' },
  avif: { kind: 'image', contentType: 'image/avif', extension: 'avif' },
  mp4: { kind: 'video', contentType: 'video/mp4', extension: 'mp4' },
  mov: { kind: 'video', contentType: 'video/quicktime', extension: 'mov' },
};

function monthOf(segments: string[], now: Date): Date | null {
  const found = segments.find((segment) => /^\d{6}$/.test(segment));
  if (!found) return null;
  const year = Number(found.slice(0, 4));
  const month = Number(found.slice(4));
  if (year < 2010 || year > now.getUTCFullYear() + 1 || month < 1 || month > 12) return null;
  return new Date(Date.UTC(year, month - 1, 1));
}

/** Strips the download's wrapping folder, if any, from an entry name. */
export function keyOf(name: string, root: string): string {
  return root && name.startsWith(root) ? name.slice(root.length) : name;
}

export function catalogArchive(
  parts: ZipEntry[][],
  facts: ReadonlyMap<string, MediaFacts> = new Map(),
  now: Date = new Date(),
): ArchiveCatalog {
  const skipped = { outsideMedia: 0, unsupported: 0, unreadable: 0, deleted: 0 };
  const media: ArchiveMedia[] = [];
  const seen = new Set<string>();
  parts.forEach((entries, source) => {
    const root = archiveRoot(entries.map((entry) => entry.name));
    for (const entry of entries) {
      if (entry.directory) continue;
      const key = keyOf(entry.name, root);
      const segments = key.split('/');
      if (segments[0] !== 'media' || segments.length < 2) { skipped.outsideMedia += 1; continue; }
      if (/^recently_deleted/i.test(segments[1])) { skipped.deleted += 1; continue; }
      const extension = (segments[segments.length - 1].split('.').pop() ?? '').toLowerCase();
      const type = TYPES[extension];
      if (!type) { skipped.unsupported += 1; continue; }
      if (entry.unavailable === 'encrypted' || entry.unavailable === 'method') { skipped.unreadable += 1; continue; }
      // Parts of one download do not repeat files; if they ever do, keep the first.
      if (seen.has(key)) continue;
      seen.add(key);
      const fact = facts.get(key);
      const takenAt = fact?.takenAt ?? monthOf(segments.slice(1, -1), now);
      media.push({
        source,
        entry,
        key,
        kind: type.kind,
        contentType: type.contentType,
        extension: type.extension,
        category: fact?.category ?? categoryFromPath(segments[1]) ?? 'other',
        takenAt,
        exactTime: Boolean(fact?.takenAt),
        tooLarge: entry.unavailable === 'too_large' || entry.uncompressedSize > MEDIA_LIMITS[type.kind].maxBytes,
      });
    }
  });
  media.sort((a, b) => (b.takenAt?.getTime() ?? -Infinity) - (a.takenAt?.getTime() ?? -Infinity) || a.key.localeCompare(b.key));
  return { media, skipped };
}
